export enum EAffinitySource {
  Manual = "manual",
  Ai = "ai",
  /** Derived from real baskets by the nightly learning pass — evidence, not a
   * guess. Deleting one is temporary: it returns if the orders still support it. */
  Learned = "learned"
}

export enum ERecommendationReason {
  BoughtTogether = "boughtTogether",
  Affinity = "affinity",
  SameCategory = "sameCategory",
  BestSeller = "bestSeller",
  NewArrival = "newArrival",
  RecentSearch = "recentSearch",
  PopularNearby = "popularNearby",
  Discovery = "discovery"
}

export enum EProductGender {
  Women = "women",
  Men = "men",
  Unisex = "unisex",
  Kids = "kids",
  Unknown = "unknown"
}

export enum EProductSeason {
  Spring = "spring",
  Summer = "summer",
  Autumn = "autumn",
  Winter = "winter",
  AllSeason = "allSeason",
  Unknown = "unknown"
}

export enum EEnrichmentStatus {
  Pending = "pending",
  Enriched = "enriched",
  Failed = "failed",
  Skipped = "skipped"
}

/** Coverage bucket in the list view — the backend also emits "none" (never attempted). */
export enum EEnrichmentListStatusFilter {
  Enriched = "enriched",
  Unenriched = "unenriched",
  Failed = "failed",
  Skipped = "skipped"
}

/** Where a category branch's attributes come from. */
export enum EEnrichmentMode {
  None = "none",
  Vision = "vision",
  Text = "text"
}

/** Closed since the resolver matches style by equality — free text never matched. */
export enum EProductStyle {
  Casual = "casual",
  Formal = "formal",
  Traditional = "traditional",
  Sporty = "sporty",
  Elegant = "elegant",
  Streetwear = "streetwear"
}

export interface AffinityRule {
  id: string;
  fromCategoryId: string;
  fromName: string | null;
  toCategoryId: string;
  toName: string | null;
  weight: number;
  source: EAffinitySource;
}

export interface AffinityBlacklistEntry {
  id: string;
  fromCategoryId: string;
  fromName: string | null;
  toCategoryId: string;
  toName: string | null;
  reason: string | null;
  createdAt: string;
}

/** Either side may be a parent category — the block then covers its whole branch. */
export interface CreateAffinityBlacklistPayload {
  fromCategoryId: string;
  toCategoryId: string;
  bothDirections?: boolean;
  reason?: string;
}

export interface BlockAffinityResult {
  entries: AffinityBlacklistEntry[];
  removedRules: number;
}

export interface CreateAffinityPayload {
  fromCategoryId: string;
  toCategoryId: string;
  weight?: number;
}

export interface GenerateAffinityResult {
  proposed: number;
  stored: number;
}

export interface AffinityStatus {
  lastGeneratedAt: string | null;
  /** False means regenerating would rewrite identical rules — the same gate the
   * monthly automatic pass uses. */
  catalogueChanged: boolean;
  rules: {
    manual: number;
    ai: number;
    learned: number;
  };
}

export interface LearningResult {
  cooccurrencePairs: number;
  affinityRules: number;
}

export interface HydratedRecommendation {
  productId: string;
  reason: ERecommendationReason;
  shortId: string | null;
  name: string | null;
  imageUrl: string | null;
  /** Both are needed to address the product page — its route is keyed by
   * category, product and variant. Null when the product has no variant we
   * could resolve, in which case the card is not a link. */
  productVariantId: string | null;
  categoryId: string | null;
}

export interface RecommendationQueryParams
  extends Record<string, string | number | undefined> {
  limit?: number;
}

/** Row status in the coverage list — free string from the backend ("none" = never attempted). */
export type EnrichmentListItemStatus =
  | "enriched"
  | "pending"
  | "failed"
  | "none"
  | string;

export interface EnrichmentListItem {
  productId: string;
  shortId: string | null;
  name: string | null;
  status: EnrichmentListItemStatus;
  enrichedAt: string | null;
  imageUrl?: string | null;
  edited: boolean;
}

export interface EnrichmentListResult {
  items: EnrichmentListItem[];
  total: number;
}

export interface GetEnrichmentListParams
  extends Record<string, string | number | undefined> {
  status?: EEnrichmentListStatusFilter;
  search?: string;
  categoryId?: string;
  profileKey?: string;
  gender?: EProductGender;
  season?: EProductSeason;
  style?: EProductStyle;
  edited?: string;
  page?: number;
  take?: number;
}

export interface ProductAttributes {
  id: string;
  productId: string;
  gender: EProductGender | null;
  itemType: string | null;
  material: string | null;
  style: string | null;
  season: EProductSeason | null;
  dominantColors: string[] | null;
  /** Where every non-general vertical keeps its attributes. Render generically:
   * a new vertical adds keys here without a backend change. */
  traits: Record<string, unknown> | null;
  profileKey: string | null;
  status: EEnrichmentStatus;
  enrichmentModel: string | null;
  enrichedAt: string | null;
  /** Attributes set or cleared by hand — column names, or `traits.<key>`.
   * Enrichment leaves these alone until they are reverted. */
  manualFields: string[];
  editedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Only the keys sent are changed; `null` clears an attribute. */
export interface UpdateProductAttributesPayload {
  gender?: EProductGender | null;
  itemType?: string | null;
  material?: string | null;
  style?: EProductStyle | null;
  season?: EProductSeason | null;
  dominantColors?: string[] | null;
  traits?: Record<string, string | null>;
}

export interface EnrichedAttributes {
  gender?: EProductGender | null;
  itemType?: string | null;
  material?: string | null;
  style?: string | null;
  season?: EProductSeason | null;
  dominantColors?: string[] | null;
}

export interface EnrichBatchResult {
  processed: number;
  enriched: number;
  skippedNoImage: number;
  failed: number;
}

export interface EnrichBatchPayload {
  limit?: number;
}

export interface SuggestionDiscountSettings {
  isEnabled: boolean;
  validityHours: number;
}

export interface UpdateSuggestionDiscountSettingsPayload {
  isEnabled?: boolean;
  validityHours?: number;
}

export interface CategoryEnrichmentProfile {
  id: string;
  categoryId: string;
  mode: EEnrichmentMode;
  profileKey: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AvailableEnrichmentProfile {
  key: string;
  label: string;
  defaultMode: EEnrichmentMode;
  fields: string[];
  /** Allowed values per trait; a trait missing here is free text. */
  vocabularies: Record<string, string[]>;
}

export interface SetCategoryProfilePayload {
  categoryId: string;
  mode: EEnrichmentMode;
  profileKey?: string;
}
